import {
  SortableItemType,
  SortableListType,
} from 'components/sortables/Sortable';
import { sortByFareyOrder } from './farey-sorting';
import {
  AnyIdObject,
  AnyObject,
  AnyOrderObject,
  orderedObjectType,
} from './objects';

// const letters = '0123456789'; // base 10
const lettersunsorted =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789abcdefghijklmnopqrstuvwxyz'; // base 26+
export const letters = lettersunsorted.split('').sort().join('');

function half(xx: string) {
  let x = xx.split('');
  let c = '';
  let carry = 0;
  for (let i = 0; i < x.length || carry !== 0; i++) {
    const digit = i < x.length ? letters.indexOf(x[i]) : 0;
    const value = digit / 2 + carry * letters.length;
    c += letters[Math.floor(value)];
    carry = value - Math.floor(value);
  }
  return c;
}

// we can safely assume that we do not overflow the most important bit
function add(aa: string, bb: string) {
  let a = aa.split('');
  let b = bb.split('');
  let c = '';
  let carry = 0;
  for (let i = Math.max(a.length, b.length) - 1; i >= 0; i--) {
    const aa = i < a.length ? letters.indexOf(a[i]) : 0;
    const bb = i < b.length ? letters.indexOf(b[i]) : 0;
    const value = aa + bb + carry;
    c = letters[value % letters.length] + c;
    carry = Math.floor(value / letters.length);
  }
  return c;
}

export function average(a: string, b: string) {
  return add(half(a), half(b));
}

export function hasFareyOrder(items: AnyOrderObject[]): boolean {
  // return false;
  return items.some(
    (item) =>
      item.hasOwnProperty('farey_order') &&
      item.farey_order != null &&
      item.farey_order !== '',
  );
}

export function sortByOrder(items: AnyOrderObject[]): AnyOrderObject[] {
  if (!items || items.length === 0) return items;
  if (hasFareyOrder(items)) {
    // If using farey_order
    return sortByFareyOrder(items);
  }
  console.log(
    '⚠️ Sorting by old order attribute for',
    orderedObjectType(items[0]),
  );
  return sortBy(
    items,
    'order',
    'ASC',
    'sensitive',
    'id',
    'ASC',
  ) as AnyOrderObject[];
}

export function sortById(
  items: AnyObject[],
  direction: 'ASC' | 'DESC' = 'ASC',
): AnyObject[] {
  return sortBy(items, 'id', direction);
}

export function sortByTime(
  items: AnyObject[],
  key: string = 'time',
  direction: 'ASC' | 'DESC' = 'ASC',
): AnyObject[] {
  const timeItems = items.map((item: AnyObject) => ({
    ...item,
    [key]: new Date(item[key]),
  }));
  return sortBy(timeItems, key, direction, (o) => {
    return new Date(o as Date);
  });
}

export function sortByTimeAndNull(
  items: AnyObject[],
  key: string = 'time',
  direction: 'ASC' | 'DESC' = 'ASC',
): AnyObject[] {
  if (!items) return [];
  const ordered = [...items];
  ordered.sort((a, b) => {
    const av = a[key] == null ? null : new Date(a[key]);
    const bv = b[key] == null ? null : new Date(b[key]);
    if (av === null && bv === null) {
      // when both are null, sort by id as secondary key using same direction
      const aid = (a.id ?? 0) as number;
      const bid = (b.id ?? 0) as number;
      if (aid < bid) return direction === 'ASC' ? -1 : 1;
      if (aid > bid) return direction === 'ASC' ? 1 : -1;
      return 0;
    }
    if (av === null) return -1; // nulls always first
    if (bv === null) return 1;
    if (av < bv) return direction === 'ASC' ? -1 : 1;
    if (av > bv) return direction === 'ASC' ? 1 : -1;
    // if dates are equal, fall back to id to provide a stable order
    const aid = (a.id ?? 0) as number;
    const bid = (b.id ?? 0) as number;
    if (aid < bid) return direction === 'ASC' ? -1 : 1;
    if (aid > bid) return direction === 'ASC' ? 1 : -1;
    return 0;
  });
  return ordered;
}

export function sortBy(...args: Parameters<typeof sortByMutating>) {
  const [items, ...rest] = args;
  const ordered = [...items];
  return sortByMutating(ordered, ...rest);
}

// sorts the given items by the given key in the given direction
// mutates the given items array
// transform can be 'insensitive' | 'sensitive' | function
// if omitted, 'insensitive' is used for string values
export function sortByMutating(
  items: AnyObject[],
  key: string,
  direction: 'ASC' | 'DESC' = 'ASC',
  transform?: ((v: any) => any) | ('insensitive' | 'sensitive'),
  key2?: string,
  direction2?: 'ASC' | 'DESC',
  transform2?: ((v: any) => any) | ('insensitive' | 'sensitive'),
): AnyObject[] {
  if (items == null) return [];
  // if transform is not provided, default to 'sensitive' for 'order' key
  const transform1 = (transform ?? key === 'order') ? 'sensitive' : undefined;
  const transform2a =
    (transform2 ?? key2 === 'order') ? 'sensitive' : undefined;
  try {
    items.sort((a, b) => {
      const aval =
        typeof transform === 'function'
          ? transform(a[key])
          : transform1 !== 'sensitive' && typeof a[key] === 'string'
            ? a[key].toLowerCase()
            : a[key];
      const bval =
        typeof transform === 'function'
          ? transform(b[key])
          : transform1 !== 'sensitive' && typeof b[key] === 'string'
            ? b[key].toLowerCase()
            : b[key];
      let res = // TODO check if aval xor bval === null
        (aval === bval
          ? 0
          : aval === null && bval !== null
            ? direction === 'DESC'
              ? -1
              : 1
            : aval !== null && bval === null
              ? direction === 'DESC'
                ? 1
                : -1
              : aval < bval
                ? -1
                : aval > bval
                  ? 1
                  : 0) * (direction === 'DESC' ? -1 : 1);
      if (res === 0 && key2) {
        const a2val =
          typeof transform2 === 'function'
            ? transform2(a[key2])
            : transform2a !== 'sensitive' && typeof a[key2] === 'string'
              ? a[key2].toLowerCase()
              : a[key2];
        const b2val =
          typeof transform2 === 'function'
            ? transform2(b[key2])
            : transform2a !== 'sensitive' && typeof b[key2] === 'string'
              ? b[key2].toLowerCase()
              : b[key2];
        res =
          (a2val < b2val ? -1 : a2val > b2val ? 1 : 0) *
          (direction2 === 'DESC' ? -1 : 1);
      }
      return res;
    });
    return items;
  } catch {
    throw new Error(
      `Error: failed to order items by '${key}' that have no such attribute.`,
    );
  }
}

export function excludeKeys(
  items: AnyObject[],
  key: string,
  exclude: any[], // array of values for key to exclude, e.g., array of id's when key === 'id'
) {
  return items.filter((item) => !exclude.includes(item[key]));
}

/*
 ** Functions for calculating order attribute when inserting/moving items in a list
 ** Uses a system of fractional indexing with strings to allow for infinite insertions without needing to reorder existing items
 */

// get the new order attribute
// for moving the item from fromIndex to toIndex.
export function getOrderUsingIndex(
  items: AnyOrderObject[],
  from: number, // -1 when moving between lists
  to: number, // -1 when moving to end of list
  before?: boolean,
): string {
  // if (hasFareyOrder(items)) {
  //   return getFareyOrderUsingIndex(items, from, to, before);
  // }
  const len = items.length;
  if (len === 0) return letters[1];
  if (to > len || from >= len) throw new Error(`Invalid index for sorting.`);
  // prettier-ignore
  let pre =
    to === 0
      ? before
        ? -1
        : to
      : from < 0
        ? to < 0
          ? len > 0
            ? len - 1
            : -1
          : before
            ? to - 1
            : to
        : from < to
          ? before
            ? to - 1
            : to
          : before
            ? to - 1
            : to;
  if (from === pre + 1) pre = from;
  // prettier-ignore
  const post =
    from >= 0 && from === pre
    ? pre
    : pre < 0
      ? 0
      : pre < len - 1
        ? pre + 1
        : -1;
  const preOrder = pre < 0 ? letters[0] : items[pre].order;
  const postOrder = post < 0 ? letters[letters.length - 1] : items[post].order;
  const newOrder = pre !== post ? average(preOrder, postOrder) : preOrder;
  return newOrder;
}

// get the new order attribute
// for moving the item with fromId to position of toId.
export function getOrderUsingId(
  items: AnyOrderObject[],
  fromId: number,
  toId: number,
  before?: boolean,
): string {
  // if (hasFareyOrder(items)) {
  //   return getFareyOrderUsingId(items, fromId, toId, before);
  // }
  const fromIndex = items.findIndex((item) => item.id === fromId);
  const toIndex = items.findIndex((item) => item.id === toId);
  return getOrderUsingIndex(items, fromIndex, toIndex, before);
}

export function getOrderForLast(
  items: AnyOrderObject[],
  fromIndex: number = -1, // -1 for new list
): string {
  // if (hasFareyOrder(items)) {
  //   return getFareyOrderForLast(items, fromIndex);
  // }
  if (!items.length) return letters[1];
  const sorted = sortByOrder(items);
  // return getOrderUsingIndex(sorted, fromIndex, sorted.length - 1);
  return getOrderUsingIndex(sorted, fromIndex, -1);
}

export function getNextOrder(items: AnyOrderObject[], alpha: boolean): string {
  // if (hasFareyOrder(items)) {
  //   return getNextFareyOrder(items);
  // }
  const start = alpha ? letters.indexOf('B') : 1;
  if (!items.length) return letters[start];
  const sorted = sortByOrder(items);
  if (sorted.length >= letters.length - start - 1) {
    return getOrderUsingIndex(sorted, 0, sorted.length - 1);
  }
  return letters[sorted.length + start];
}

export function getOrderByIndex(
  nextIndex: number = -1, // -1 for new list
): string {
  if (nextIndex < 1) return letters[1];
  return letters[1 + nextIndex];
}

export function getNextOrderAfterLast(
  lastOrder: string | null,
  alpha: boolean,
): string {
  if (!lastOrder || lastOrder === '') return alpha ? 'B' : '1';
  const items = [{ order: lastOrder, id: 0, farey_order: '' }];
  return getNextOrder(items, alpha);
}

// TODO: check if all of the below is still in use, if not: purge
export type UniqueId = string;
export type UniqueItem = {
  id: UniqueId;
  object: any;
};
export function toUniqueId(
  id: number | number[],
  type: SortableListType | SortableItemType,
): UniqueId {
  return Array.isArray(id)
    ? (type + '_').concat(id.join('_'))
    : type + '_' + id;
}
export function toUniqueIds(
  arr: AnyIdObject[],
  type: SortableListType | SortableItemType,
): UniqueId[] {
  return arr.map((item) => toUniqueId(item.id, type));
}
export function fromUniqueId(id: UniqueId): {
  id: number;
  relatedIds?: number[];
  type: SortableListType | SortableItemType;
} {
  const s: string[] = id.split('_');
  if (s.length < 2) return { id: 0, type: SortableItemType.SortableItem };
  return {
    id: parseInt(s[1]),
    type: s[0] as SortableListType | SortableItemType,
    relatedIds: s.length > 2 ? s.slice(2).map((i) => parseInt(i)) : undefined,
  };
}
export function fromUniqueIds(arr: UniqueId[]): {
  type: SortableListType | SortableItemType;
  ids: number[];
} {
  let type: SortableListType | SortableItemType | undefined = undefined;
  return {
    type: type! as SortableListType | SortableItemType,
    ids: arr.map((id) => {
      if (!type) type = fromUniqueId(id).type;
      return fromUniqueId(id).id;
    }),
  };
}
export function wrapForSortable(
  object: AnyIdObject[],
  type: string,
): UniqueItem[] {
  return object.map((o) => {
    return { id: type + ':' + o.id, object: o };
  });
}
export function unwrapSortable(item: UniqueItem[]): AnyIdObject[] {
  return item.map((i) => i.object);
}
export function findSortableObject(
  items: UniqueItem[],
  id: UniqueId,
): AnyIdObject | undefined {
  return items.find((item) => item.id === id)?.object;
}
export function findSortableIndex(items: UniqueItem[], id: UniqueId): number {
  return items.findIndex((item) => item.id === id);
}

export function findAncestor(
  element: HTMLElement,
  className: string,
): HTMLElement {
  let ancestor: HTMLElement = element;
  while (ancestor !== null) {
    if (ancestor.classList.contains(className)) {
      return ancestor;
    }
    if (ancestor.parentElement) ancestor = ancestor.parentElement;
    else break;
  }
  return ancestor;
}

export function findSibling(
  element: HTMLElement,
  className: string,
): HTMLElement | null {
  const parent = element.parentElement;
  if (parent) return findChild(parent, className);
  return null;
}

export function findChild(
  element: HTMLElement,
  className: string,
): HTMLElement | null {
  const children = element.children;
  for (let i = 0; i < children.length; i++) {
    const child = children[i] as HTMLElement;
    if (child.classList.contains(className)) {
      return child;
    }
  }
  return null;
}
